import { useRef, useCallback, useContext } from 'react'
import * as React from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import LayoutContainer from 'components/Common/LayoutContainer'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Image from 'components/Common/Image'
import HeadData from 'components/Common/HeadData'
import ImageHero from 'components/Common/ImageHero'
import Faq from 'components/Common/Faq'
import { mediaQueryUp } from 'components/utils/breakpoint'
import InsuranceIcon from 'LuxKit/Icons/custom/InsuranceIcon'
import VacationIcon from 'LuxKit/Icons/custom/VacationIcon'
import { useInsuranceProductsData } from './CoverGeniusData'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import { connect } from 'react-redux'
import useDelayedJumpToHash from 'hooks/useDelayedJumpToHash'
import { addGTMEvent } from 'api/googleTagManager'
import {
  insuranceLandingPageViewCoverOptionsClick,
  insuranceLandingPageGetQuoteClick,
} from 'analytics/eventDefinitions'
import { capitalise } from 'lib/string/stringUtils'
import TextButton from 'LuxKit/Button/TextButton'
import TextLink from 'LuxKit/TextLink'
import Group from 'components/utils/Group'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import PageBanner from 'LuxKit/Banners/PageBanner'
import LuxPlusLogoWrapper from 'luxPlus/components/LuxPlusLogoWrapper'
import { LUXURY_PLUS_PATHS } from 'luxPlus/constants/base'
import { getThemeClassName } from 'lib/theme/themeUtils'
import { getIsInsuranceAndRefundProtectionLuxPlusBenefitsEnabled, getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import FormatCurrency from 'components/Common/FormatCurrency'
import ContactContext from 'contexts/contactContext'

const HeroImage = styled(ImageHero)`
  max-height: ${rem(300)};
  min-height: ${rem(240)};

  ${mediaQueryUp.tablet} {
    min-height: ${rem(220)};
  }

  ${mediaQueryUp.desktop} {
    min-height: ${rem(200)};
  }
`

const Section = styled.div`
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};

  &:nth-child(even) {
    background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  }
`

const InnerSection = styled(LayoutContainer).attrs<any>({
  size: 'md',
})`
  padding: ${rem(32)} ${rem(40)};

  ${mediaQueryUp.desktop} {
    padding: ${rem(60)} ${rem(32)};
  }
`

const FaqInnerSection = styled(InnerSection)`
  padding-top: ${rem(16)};
`

const SectionBlurb = styled(BodyText)`
  margin-top: ${rem(16)};
`

const SubSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: ${rem(24)};

  ${mediaQueryUp.desktop} {
    margin-top: ${rem(40)};
  }

  > * + * {
    margin-top: ${rem(16)};
  }
`

const SectionContent = styled.div`
  margin-top: ${rem(32)};
  width: 100%;
`

const InsuranceProductBody = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${rem(20)} ${rem(24)};
  max-width: ${rem(320)};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  justify-content: space-between;
`

const InsuranceProductButtons = styled.div`
  margin-top: ${rem(18)};
`

const ProductTitle = styled(BodyText)`
  margin-top: ${rem(5)};
`

const ProductBlurb = styled(BodyText)`
  margin-top: ${rem(12)};
`

const CenteredGallery = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;

  ${mediaQueryUp.desktop} {
    flex-direction: row;
    justify-content: center;
    align-items: stretch;
  }
`

const ProductsGallery = styled(CenteredGallery)`
  > * + * {
    margin-top: ${rem(16)};
  }

  ${mediaQueryUp.desktop} {
    > * + * {
      margin-left: ${rem(16)};
      margin-top: 0;
    }
  }
`

const WhyBuyGallery = styled(CenteredGallery)`
  gap: ${rem(32)};
  width: 100%;

  > * {
    max-width: ${rem(360)};
    width: 100%;
    flex: 1 1 ${rem(280)};
  }

  ${mediaQueryUp.desktop} {
    flex-wrap: wrap;
    justify-content: center;
  }
`

const WhyBuyLogo = styled(Image)<{ $width?: number; $height?: number }>`
  width: ${props => (props.$width ? rem(props.$width) : 'auto')};
  height: ${props => (props.$height ? rem(props.$height) : rem(42))};
  max-width: 100%;
  margin: 0 auto;
  display: block;

  img {
    object-fit: contain;
  }
`

const WhyBuyContainer = styled.div`
  text-align: center;
  border: solid 1px ${props => props.theme.palette.neutral.surface.lightForeground};
  padding: ${rem(20)} ${rem(16)} ${rem(16)};

  >:nth-child(2) {
    margin-top: ${rem(24)};
  }

  >:nth-child(3) {
    margin-top: ${rem(8)};
  }
`

const WhyByVisual = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  height:  ${rem(50)};
`

const TickItems = styled.div`
  text-align: left;
  margin-top: ${rem(16)};

  > * + * {
    margin-top: ${rem(8)};
  }
`

const HeroSectionContent = styled(InnerSection)`
  align-items: center;
  justify-content: center;
  position: relative;
  padding: ${rem(16)} ${rem(24)};

  ${mediaQueryUp.desktop} {
    padding: ${rem(24)} ${rem(32)};
  }
`

const HeroBlurb = styled(Heading)`
  margin-top: ${rem(12)};
`

const HeroButton = styled(TextButton)`
  margin-left: auto;
  margin-right: auto;
  margin-top: ${rem(16)};
  display: block;
`

const CallUsBlock = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;

  > * + * {
    margin-top: ${rem(8)};
  }

  ${mediaQueryUp.desktop} {
    flex-direction: row;

    > * + * {
      margin-left: ${rem(8)};
      margin-top: 0;
    }
  }
`

const PlusText = styled(Heading)`
  color: ${props => props.theme.palette.brand.primary.normal};
`

interface MappedProps {
  insuranceProductName: string
  regionCode: string
  luxPlusEnabled: boolean
  insuranceAndRefundProtectionEnabled: boolean
}

interface Props extends MappedProps {}

function TravelInsurancePage(props: Props) {
  const { insuranceProductName, regionCode, luxPlusEnabled, insuranceAndRefundProtectionEnabled } = props

  const intl = useIntl()
  const insuranceProducts = useInsuranceProductsData()
  const contact = useContext(ContactContext)
  const comparePlansRef = useRef<HTMLDivElement>(null)

  const onViewOptionsClick = useCallback(() => {
    comparePlansRef.current?.scrollIntoView()

    addGTMEvent(insuranceLandingPageViewCoverOptionsClick())
  }, [])

  const onGetQuote = useCallback(() => {
    addGTMEvent(insuranceLandingPageGetQuoteClick())
  }, [])

  useDelayedJumpToHash()

  return (
    <>
      {luxPlusEnabled && insuranceAndRefundProtectionEnabled && <PageBanner
        kind="lux-plus"
        className={getThemeClassName({ mobile: 'inverse' })}
        align="center"
      >
        <Group direction="vertical" desktopDirection="horizontal" horizontalAlign="center" gap={12} desktopGap={20}>
          <LuxPlusLogoWrapper height={18} />
          <div>
            <FormattedMessage
              id="dlYNIUTJi2yW3EWYBOet"
              defaultMessage="Members save 40% on Travel & Cancellation Protection^ <link>View all benefits</link>"
              description="LuxPlus banner promoting insurance discount with link to view all benefits"
              values={{
                link: (chunks: React.ReactNode) => <TextLink weight="bold" to={`/${LUXURY_PLUS_PATHS.LANDING_PAGE}`}>{chunks}</TextLink>,
              }}
            />
          </div>
        </Group>
      </PageBanner>}
      <HeadData title={intl.formatMessage({ id: 'eh78KXS_jRkHASgks3uF', defaultMessage: 'Travel {insuranceProductName}', description: 'Page title for travel insurance/protection landing page' }, { insuranceProductName: capitalise(insuranceProductName) })} />
      <HeroImage
        alt={intl.formatMessage({ id: 'dh8tng2Wd1h-iuVGqZ36', defaultMessage: 'Travel {insuranceProductName} Banner', description: 'Alt text for travel insurance/protection hero banner image' }, { insuranceProductName: capitalise(insuranceProductName) })}
        aria-hidden="true"
        brightness={0}
        gravity="center"
        mobileAspectRatio="16:15"
        tabletAspectRatio="3:2"
        desktopAspectRatio="16:4"
        largeDesktopAspectRatio="18:5"
        id="23sfdwahze86vktpobuh"
      >
        <HeroSectionContent>
          <Heading variant="heading2" align="center" colour="dark-contrast" format="titlecase">
            {intl.formatMessage({ id: 'C28RN4XTRjlBroYlyuHi', defaultMessage: 'Luxury Escapes Travel\u00a0{insuranceProductName}', description: 'Hero heading for travel insurance/protection landing page' }, { insuranceProductName })}
          </Heading>
          <HeroBlurb variant="heading3" align="center" colour="dark-contrast" forwardedAs="h2">
            {intl.formatMessage({ id: 'I0Pj0TEUnNjoQoXma41c', defaultMessage: 'Prepare for the unexpected', description: 'Hero subheading for travel insurance/protection landing page' })}
          </HeroBlurb>
          <HeroButton kind="primary" onClick={onViewOptionsClick}>
            {intl.formatMessage({ id: 'yJAdqaR6LGfMUNnuMlrW', defaultMessage: 'View cover options', description: 'Hero button to scroll to insurance cover options on the promotional landing page' })}
          </HeroButton>
        </HeroSectionContent>
      </HeroImage>
      <Section>
        <InnerSection>
          <LayoutContainer size="sm">
            <Heading variant="heading3" align="center">
              {intl.formatMessage({ id: 'An0j29rVsTDVUBvEAJ19', defaultMessage: 'Protect against the unexpected on your Luxury Escape', description: 'Section heading about protection on travel insurance landing page' })}
            </Heading>
          </LayoutContainer>
          <SectionBlurb variant="large" align="center">
            {intl.formatMessage({ id: 'SC-iJRrmLcOIwjePzjuG', defaultMessage: 'Accidents can happen to anyone and can leave you out of pocket. Travel {insuranceProductName} may offer {insuranceProductName} when things go wrong before or during your travels. Benefits may include: travel delays or cancellations, medical and hospital cover, and protection for lost, stolen or damaged belongings. Can you afford the risk of exploring the world without travel {insuranceProductName}?', description: 'Description of travel insurance benefits and risks on landing page' }, { insuranceProductName })}
          </SectionBlurb>
        </InnerSection>
      </Section>
      <Section ref={comparePlansRef}>
        <InnerSection>
          <Heading variant="heading3" align="center" format="titlecase">{intl.formatMessage({ id: 'HxKYhKs79U0fq2cWdZMd', defaultMessage: 'Compare our Travel\u00a0{insuranceProductName}\u00a0Plans', description: 'Section heading for comparing travel insurance plans' }, { insuranceProductName })}</Heading>
          <SectionBlurb variant="large" align="center">{intl.formatMessage({ id: 'bs5DSHDbXZ33df-SxOph', defaultMessage: 'We have three plans with different levels of cover.', description: 'Subtext for the insurance plan comparison section on the promotional landing page' })}</SectionBlurb>

          <SectionContent>
            <ProductsGallery>
              {insuranceProducts.map(product => (
                <InsuranceProductBody key={product.title}>
                  <div>
                    <BodyText variant="medium" format="uppercase" align="center">{product.type}</BodyText>
                    <ProductTitle variant="medium" weight="bold" align="center" forwardedAs="h3">{product.title}</ProductTitle>
                    {product.blurb && <ProductBlurb variant="medium">{product.blurb}</ProductBlurb>}
                    <TickItems>
                      {product.benefits.map((benefit, index) => <BodyTextBlock
                        key={index}
                        variant="medium"
                        startIcon={<LineCheckIcon colour="success" />}
                      >
                        {benefit.text}
                      </BodyTextBlock>)}
                      {product.complementaryText && <BodyText variant="medium">{product.complementaryText}</BodyText>}
                    </TickItems>
                  </div>
                  <InsuranceProductButtons>
                    <TextButton
                      kind="primary"
                      data-testid="get-a-quote"
                      fit="full-width"
                      onClick={onGetQuote}
                      to="/travel-protection/quote"
                    >
                      {intl.formatMessage({ id: '6g3N9gf_A6yKuvewWGCv', defaultMessage: 'Get a Quote', description: 'Button text to get an insurance quote on landing page' })}
                    </TextButton>
                  </InsuranceProductButtons>
                </InsuranceProductBody>
              ))}
            </ProductsGallery>
          </SectionContent>
          <SubSection>
            <BodyText variant="large">{intl.formatMessage({ id: 'GM_VJ0oN2kdJkpCGeUxf', defaultMessage: 'Need more information about Luxury Escapes Travel {insuranceProductName}?', description: 'Prompt to contact support for more insurance information' }, { insuranceProductName: capitalise(insuranceProductName) })} </BodyText>
            <CallUsBlock>
              <BodyText variant="large" weight="bold"> {intl.formatMessage({ id: 'LCJfvcMpToTS5i8EjhZR', defaultMessage: 'Call us on', description: 'Label before phone number for insurance support on the promotional landing page' })} </BodyText>
              <TextLink startIcon={<LinePhoneIcon />} href={`tel:${contact.defaultContact.number}`}>
                {contact.defaultContact.humanReadable}
              </TextLink>
            </CallUsBlock>
          </SubSection>
        </InnerSection>
      </Section>
      <Section>
        <InnerSection>
          <Heading variant="heading3" align="center">{intl.formatMessage({ id: 'w4cusrE87P1JTmbXYJOk', defaultMessage: 'Why buy travel {insuranceProductName} with us?', description: 'Section heading asking why to buy travel insurance with Luxury Escapes' }, { insuranceProductName })}</Heading>
          <SectionBlurb variant="large" align="center">{intl.formatMessage({ id: 'Sx7IlLMscK3uAYpXEYxg', defaultMessage: 'Protecting the world’s greatest holidays with reputable travel {insuranceProductName}', description: 'Subtitle for why-buy section on travel insurance landing page' }, { insuranceProductName })}</SectionBlurb>
          <SectionContent>
            <WhyBuyGallery>
              <WhyBuyContainer>
                <WhyByVisual>
                  <Group direction="horizontal" verticalAlign="center" horizontalAlign="center" gap={8}>
                    <VacationIcon colour="primary" size={48} />
                    <PlusText variant="heading3">{intl.formatMessage({
                      id: 'O4wJodii-mA3GOWqsRnw',
                      defaultMessage: '+',
                      description: 'Plus sign between vacation and insurance icons on the promotional landing page',
                    })}
                    </PlusText>
                    <InsuranceIcon colour="primary" size={48} />
                  </Group>
                </WhyByVisual>
                <Heading variant="heading6" format="titlecase" as="h3">
                  {intl.formatMessage({ id: 'ButLMwir2QetT8724oUg', defaultMessage: 'Bundle Your Holiday + Travel {insuranceProductName}', description: 'Heading for bundling holiday with travel insurance' }, { insuranceProductName })}
                </Heading>
                <BodyText variant="large">
                  {intl.formatMessage({ id: 'IUtiebP-grSdRYfrVUqa', defaultMessage: 'Easily add travel {insuranceProductName} to your upcoming Luxury Escape.', description: 'Description for bundling travel insurance with holiday booking' }, { insuranceProductName })}
                </BodyText>
              </WhyBuyContainer>
              <WhyBuyContainer>
                <WhyByVisual>
                  <WhyBuyLogo
                    id="34vqalq5dzj565v16rzb"
                    alt={intl.formatMessage({
                      id: 'aHqbw2AJFYpeWvMcDbxL',
                      defaultMessage: 'XCover',
                      description: 'Alt text for the XCover partner logo on the insurance landing page',
                    })}
                    fit="center"
                    objectFit="contain"
                    $width={200}
                    $height={42}
                  />
                </WhyByVisual>
                <Heading variant="heading6" as="h3">{intl.formatMessage({ id: '7a7zcy4OtuID-r5kyCdg', defaultMessage: 'We’ve partnered with XCover', description: 'Heading for XCover partnership on insurance landing page' })}</Heading>
                <BodyText variant="large">{intl.formatMessage({ id: '3xAub9tUUevJHntXRxTR', defaultMessage: 'Over 3 million customers trust XCover for fast, stress-free protection.', description: 'Description of XCover partnership trust on insurance landing page' })}</BodyText>
              </WhyBuyContainer>
              {regionCode === 'AU' && (
                <WhyBuyContainer>
                  <WhyByVisual>
                    <WhyBuyLogo
                      id="2eff7488-24d1-4b70-93bf-e6b37fc43cab"
                      alt={intl.formatMessage({
                        id: 'nG5-3AZeyyAyNbqbw4ri',
                        defaultMessage: 'PassportCard',
                        description: 'Alt text for the PassportCard partner logo on the insurance landing page',
                      })}
                      fit="center"
                      objectFit="contain"
                      $width={200}
                      $height={42}
                    />
                  </WhyByVisual>
                  <Heading variant="heading6" as="h3">{intl.formatMessage({ id: 'ZQ9CjXb0I2rdgFFoXMLK', defaultMessage: 'We’ve partnered with PassportCard', description: 'Heading for PassportCard partnership on insurance landing page' })}</Heading>
                  <BodyText variant="large">
                    {intl.formatMessage({ id: 'lE4vlyg69z1oZDiC1aei', defaultMessage: 'PassportCard is the most awarded travel insurance of 2025^', description: 'Description of PassportCard partnership on insurance landing page' })}
                  </BodyText>
                </WhyBuyContainer>
              )}
            </WhyBuyGallery>
          </SectionContent>
        </InnerSection>
        <FaqInnerSection>
          <Heading variant="heading3" align="center" id="faqs">{intl.formatMessage({ id: 'RLSJEK7cf8JfaP3Uh3Dn', defaultMessage: 'Frequently Asked Questions', description: 'FAQ section heading on travel insurance landing page' })}</Heading>
          <SectionContent>
            <Faq slug="xcover-faqs" />
          </SectionContent>
        </FaqInnerSection>
        <InnerSection>
          <LayoutContainer size="sm">
            <Heading variant="heading3" align="center">{intl.formatMessage({ id: 'DVdtNu159Oa8tSE60Rw8', defaultMessage: 'Disclaimer', description: 'Disclaimer section heading on travel insurance landing page' })}</Heading>
          </LayoutContainer>
          <VerticalSpacer gap={40}>
            <SectionBlurb variant="small" align="center">
              {regionCode === 'AU' && (
                <FormattedMessage
                  id="QHXEv2wViCJcLjzJxY0E"
                  defaultMessage="You should consider the <link>PDS/FSG/TMD</link> and confirm that you are eligible for coverage, including the age limit. Pacific International Insurance Pty Limited ABN 83 169 311 193 is the underwriter and XCover, a trading name of Cover Genius Pty Limited ABN 43 159 983 598, AFSL 490058, is the issuer. Luxury Escapes Travel Pty Ltd ABN 16 150 650 927 is the authorised representative (AR No 001288350)."
                  description="Legal disclaimer for Australian users about insurance product disclosure on landing page"
                  values={{
                    link: (chunks: React.ReactNode) => <TextLink weight="regular" target="_blank" href="https://www.xcover.com/en/pds/luxury-escapes-au-pds">{chunks}</TextLink>,
                  }}
                />
              )}
              {regionCode === 'NZ' && (
                <FormattedMessage
                  id="_Wvm6MSINnkCmFbCTzxT"
                  defaultMessage="You should consider the <link>PDS</link> and confirm that you are eligible for coverage, including the age limit. Pacific International Insurance Pty Limited ABN 83 169 311 193 is the underwriter and XCover, a trading name of Cover Genius Pty Limited ABN 43 159 983 598, is the issuer."
                  description="Legal disclaimer for New Zealand users about insurance product disclosure on landing page"
                  values={{
                    link: (chunks: React.ReactNode) => <TextLink weight="regular" target="_blank" href="https://www.xcover.com/en/pds/luxury-escapes-au-pds">{chunks}</TextLink>,
                  }}
                />
              )}
              {regionCode !== 'AU' && regionCode !== 'NZ' && intl.formatMessage({
                id: 'col3XUL7H-JQ-GyYc2Uo',
                defaultMessage: 'You should consider the Plan Terms and confirm that you are eligible for coverage, including the age limit. The Plan is provided by Cover Genius Singapore Pte. Ltd. The Medical cover is underwritten by Isosceles PCC Limited - Cell Hotspot.',
                description: 'Legal disclaimer for international users about insurance plan terms on landing page',
              })}
            </SectionBlurb>
            {regionCode === 'AU' && (
              <BodyText variant="small" align="center">
                {intl.formatMessage({
                  id: 'oZ6-Nb0r7vbq6Rd60-Il',
                  defaultMessage: 'Luxury Escapes is an Authorised Representative (AR No. 001288350) of PassportCard Australia Pty Ltd ABN 76 621 476220 AFSL 551 057. Should you purchase a policy, Luxury Escapes may receive a commission. Any advice provided is general in nature, does not consider your individual circumstances, objectives, financial situation or needs. Terms and conditions, limits, and exclusions apply. Before making a decision, please review the combined FSG and PDS and the TMD. Reference: V1LELP20251024. ^As of 4 September 2025.',
                  description: 'PassportCard legal disclaimer for Australian users on insurance landing page',
                })}
              </BodyText>
            )}
            {luxPlusEnabled && insuranceAndRefundProtectionEnabled && <BodyText variant="small">
              <FormattedMessage
                id="aEgfsTd5PmCzoKvGOg5A"
                defaultMessage="^Members receive 40% off Travel and Cancellation Protection product(s) (Product) up to a maximum discount of {maxDiscount}. The Product may not be available to all members and eligibility and coverage restrictions apply. Members must read and consider the relevant PDS to ensure whether and how the Product applies to them. The relevant terms and PDS of the Product will apply to any purchase. The Product benefit is not exchangeable for cash or credit if not able to be utilised for any reason. <link>T&Cs apply.</link>"
                description="LuxPlus membership disclaimer about insurance discount terms and conditions on landing page"
                values={{
                  maxDiscount: <FormatCurrency value={300} format="rounded" />,
                  link: (chunks: React.ReactNode) => <TextLink weight="regular" to="/terms-and-conditions">{chunks}</TextLink>,
                }}
              />
            </BodyText>}
          </VerticalSpacer>
        </InnerSection>
      </Section>
    </>
  )
}

function mapStateToProps(state: App.State): MappedProps {
  return ({
    insuranceProductName: state.geo.insuranceProductName,
    regionCode: state.geo.currentRegionCode,
    luxPlusEnabled: getIsLuxPlusEnabled(state),
    insuranceAndRefundProtectionEnabled: getIsInsuranceAndRefundProtectionLuxPlusBenefitsEnabled(state),
  })
}

export default connect(mapStateToProps)(TravelInsurancePage)
